import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const highlights = [
  { label: "Years Experience", value: 10, suffix: "+" },
  { label: "Professionals Trained", value: 1000, suffix: "+" },
  { label: "ESG & Sustainability", value: "Specialist", isText: true },
  { label: "Security & Risk", value: "Expert", isText: true },
  { label: "Transformation", value: "Advisor", isText: true }
];

const expertise = [
  "ESG Frameworks", "Sustainability Reporting", "Corporate Training", 
  "Security Operations", "Counter-Terrorism", "Leadership Development", 
  "Risk Assessment", "Business Excellence", "Zero Waste Management", "Organisational Strategy"
];

const certifications = [
  "ACTA Certified Trainer", "Certified ESG Trainer", "Certified Sustainability Coach",
  "Certified Zero Waste Manager", "IBM Cybersecurity SkillBuild", "Advanced Diploma in Innovation & Creativity (SMU)"
];

const FounderProfile = () => {
  return (
    <section id="founder" className="py-24 md:py-32 bg-brand-black relative overflow-hidden">
      {/* Background Ambience */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-gold/5 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/4"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          
          {/* LEFT SIDE: Portrait Area */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 relative group"
          >
            <div className="relative z-10 border border-white/10 p-4 bg-white/5 backdrop-blur-sm shadow-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <img 
                src="https://media-manager-c.questera.ai/greta-media/ab03ff55dc7bcdd9e4a34a93308a2aa656b3e0baf34844f0be8f244dd21175025656600d9573a40a26a1591403a49a1d/images/aW1hZ2UvanBlZw==/e0122c5db4c2f606af3c3427c3142515.jpg" 
                alt="Martin Lim - Founder & Director" 
                className="w-full aspect-[4/5] object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700"
              />
              {/* Animated corner accent */}
              <div className="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2 border-brand-gold -translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
            </div>
            
            {/* Floating Label */}
            <div className="absolute -bottom-6 -right-6 z-20 bg-brand-gold p-6 shadow-xl">
              <p className="text-white text-xs font-black tracking-widest uppercase mb-1">Founder & Director</p>
              <h3 className="text-white text-2xl font-bold tracking-tight">Martin Lim</h3>
            </div>
          </motion.div>

          {/* RIGHT SIDE: Executive Bio */}
          <div className="lg:col-span-7 pt-10 lg:pt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1 bg-brand-gold/10 border border-brand-gold/20 text-brand-gold text-[10px] font-bold tracking-widest uppercase mb-6">
                Leadership Profile
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-8 leading-tight">
                Driving Transformation Through <br /> <span className="text-brand-gold italic">Strategic Leadership</span>
              </h2>
              
              <div className="space-y-6 text-gray-400 font-light leading-relaxed text-lg max-w-3xl">
                <p>
                  Martin Lim is the Founder & Director of White Knight Global, bringing over a decade of expertise in strategic consulting, adult education, sustainability frameworks, security operations, and organisational transformation.
                </p>
                <p>
                  With extensive experience delivering high-impact programmes for corporate and government sectors, Martin specialises in ESG advisory, sustainability coaching, leadership development, counter-terrorism preparedness, and operational excellence.
                </p>
                <p>
                  His professional background combines real-world security leadership experience with advanced training and consulting expertise, enabling organisations to strengthen resilience, improve governance, and drive sustainable long-term growth.
                </p>
              </div>

              {/* Highlight Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
                {highlights.map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-4 bg-white/5 border border-white/10 hover:border-brand-gold/30 transition-colors text-center"
                  >
                    <div className="text-2xl font-bold text-white mb-1">
                      {item.isText ? item.value : <span className="text-brand-gold">{item.value}{item.suffix}</span>}
                    </div>
                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-tight">
                      {item.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Areas of Expertise */}
        <div className="mt-32">
          <div className="flex items-center gap-6 mb-12">
            <h3 className="text-2xl font-bold text-white tracking-tight shrink-0">Areas of Expertise</h3>
            <div className="h-[1px] bg-white/10 w-full"></div>
          </div>
          <div className="flex flex-wrap gap-3">
            {expertise.map((skill, idx) => (
              <motion.span
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="px-6 py-3 bg-brand-gray/5 border border-white/5 text-gray-300 text-sm font-medium hover:border-brand-gold hover:text-white transition-all cursor-default"
              >
                {skill}
              </motion.span>
            ))}
          </div>
        </div>

        {/* Certifications & Credentials */}
        <div className="mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-white mb-8">Certifications & Credentials</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {certifications.map((cert, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white/5 border border-white/5 hover:bg-white/10 transition-all group">
                  <div className="w-8 h-8 rounded bg-brand-gold/20 flex items-center justify-center text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-all">
                    <SafeIcon name="Check" className="text-sm" />
                  </div>
                  <span className="text-sm text-gray-300 font-medium">{cert}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-10 bg-brand-gold text-white relative overflow-hidden"
          >
            <div className="relative z-10">
              <SafeIcon name="Quote" className="text-4xl opacity-30 mb-6" />
              <p className="text-2xl font-light italic leading-relaxed mb-8">
                "Martin has successfully trained and coached more than 1,000 professionals across multiple industries, supporting businesses in implementing ESG frameworks and strategic capability development."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-10 h-[1px] bg-white"></div>
                <span className="text-sm font-bold uppercase tracking-widest">Executive Impact Summary</span>
              </div>
            </div>
            {/* Background Knight Graphic */}
            <div className="absolute right-0 bottom-0 text-[200px] leading-none opacity-10 translate-x-1/4 translate-y-1/4">♞</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FounderProfile;